import React from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { LOCATION_BASE_PATH } from "main/constants/constant";
import { SvgIcon } from "main/components/icon/SvgIcon";
import {
  accentColor,
  borderColor,
  textColor,
} from "main/styles/variables/colorStyle";
import {
  fontSize,
  fontWeight,
  letterSpacing,
} from "main/styles/variables/typographyStyle";
import { space } from "main/styles/variables/spaceStyle";
import { borderRadius } from "main/styles/variables/borderStyle";
import { moreThanBreakpoint } from "main/styles/variables/responsiveStyle";

export interface IProps {
  hotelId: string;
  preBookingId?: string;
  encodedOriginUrl: string;
  canYahoojpLogin?: boolean;
  canLineLogin?: boolean;
}

export const UserSocialSignIn: React.FC<IProps> = (
  props: IProps,
): React.ReactElement => {
  const {
    hotelId,
    preBookingId,
    encodedOriginUrl,
    canLineLogin,
    canYahoojpLogin,
  } = props;
  const { t } = useTranslation(["button", "component"]);

  let lineAuthUrl = `${LOCATION_BASE_PATH}/omniauth/${hotelId}/user_auth/line?auth_origin_url=${encodedOriginUrl}`;
  let yahooAuthUrl = `${LOCATION_BASE_PATH}/omniauth/${hotelId}/user_auth/yahoojp?auth_origin_url=${encodedOriginUrl}`;

  if (preBookingId) {
    lineAuthUrl = `${lineAuthUrl}&pre_booking_id=${preBookingId}`;
    yahooAuthUrl = `${yahooAuthUrl}&pre_booking_id=${preBookingId}`;
  }

  return (
    <div css={blockStyle}>
      <div css={socialSignInTitleStyle}>
        {t("component:user.socialSignInTitle")}
      </div>
      <div css={buttonBlockStyle}>
        {canLineLogin ? (
          <a css={lineButtonStyle} href={lineAuthUrl}>
            <div css={iconStyle}>
              <SvgIcon
                name="line"
                size="1.75rem"
                color={accentColor.lineColor}
              />
            </div>
            <div css={textStyle}>{t("button:user.signInWithLine")}</div>
          </a>
        ) : null}
        {canYahoojpLogin ? (
          <a css={yahooJapanButtonStyle} href={yahooAuthUrl}>
            <div css={iconStyle}>
              <SvgIcon
                name="yahooJapan"
                size="1.75rem"
                color={accentColor.yahooJapanColor}
              />
            </div>
            <div css={textStyle}>{t("button:user.signInWithYahooJapan")}</div>
          </a>
        ) : null}
      </div>
    </div>
  );
};

const blockStyle = css`
  padding: ${space.atom};
`;
const socialSignInTitleStyle = css`
  font-size: ${fontSize.caption};
  color: ${textColor.secondaryDarkColor};
  text-align: center;
  display: flex;
  align-items: center;
  padding: ${space.atom} 0;
  &:before,
  &:after {
    content: "";
    display: block;
    height: 0.0625rem;
    flex: 1;
    background-color: ${borderColor.secondaryLightColor};
  }
  &:before {
    margin-right: ${space.atom2_5x};
  }
  &:after {
    margin-left: ${space.atom2_5x};
  }
`;
const buttonBlockStyle = css`
  padding-top: ${space.atom2x};
  max-width: 20rem;
  margin: 0 auto;
  ${moreThanBreakpoint("tablet")} {
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;
const buttonStyle = css`
  display: flex;
  align-items: center;
  font-size: ${fontSize.body};
  font-weight: ${fontWeight.medium};
  letter-spacing: ${letterSpacing.text};
  border-radius: ${borderRadius.normal};
  color: ${textColor.primaryDarkColor};
  border: 0.125rem solid;
  overflow: hidden;
  transition: color 0.4s ease;
  margin-bottom: ${space.atom2x};
  ${moreThanBreakpoint("tablet")} {
    width: 48%;
  }
`;
const lineButtonStyle = css`
  ${buttonStyle};
  &:hover {
    color: ${accentColor.lineColor};
  }
`;
const yahooJapanButtonStyle = css`
  ${buttonStyle};
  font-size: ${fontSize.caption};
  &:hover {
    color: ${accentColor.yahooJapanColor};
  }
`;
const iconStyle = css`
  margin-right: ${space.atom};
  padding: ${space.atom0_5x} ${space.atom};
`;
const textStyle = css``;
